import { iReview, iLike, iComment } from '@/entities/review/review.types';
import { Review } from '@/entities/review/review';
import { Like } from '@/entities/review/like';
import { Share } from '@/entities/review/share';
import { Comment } from '@/entities/review/comment';
import { iFirestoreReviewModel } from '../firestore.types';

const REVIEW_COLLECTION = 'review';
const LIKE_SUBCOLLECTION = 'like';
const COMMENT_SUBCOLLECTION = 'comment';

type ReviewSnapshot = firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>;

export class FirestoreReviewModel implements iFirestoreReviewModel {
  private db: firebase.firestore.Firestore;

  constructor(db: firebase.firestore.Firestore) {
    this.db = db;
  }

  async create(review: iReview): Promise<iReview> {
    const reviewRef = this.db.collection(REVIEW_COLLECTION)
      .doc(review.id);

    await reviewRef.set(review.toObject());

    return review;
  }

  async findByGameId(gameId: number): Promise<iReview[]> {
    const snapshot = await this.db.collection(REVIEW_COLLECTION)
      .where('gameId', '==', +gameId)
      .orderBy('createdAt', 'desc')
      .limit(100)
      .get();

    return this.buildReviewsFromSnapshot(snapshot);
  }

  async like(reviewId: string, like: iLike): Promise<iLike> {
    const reviewLikeRef = this.db.collection(REVIEW_COLLECTION)
      .doc(reviewId)
      .collection(LIKE_SUBCOLLECTION)
      .doc(like.id);

    await reviewLikeRef.set(like.toObject());

    return like;
  }

  async unlike(reviewId: string, userId: string): Promise<void> {
    const snapshot = await this.db.collection(REVIEW_COLLECTION)
      .doc(reviewId)
      .collection(LIKE_SUBCOLLECTION)
      .where('userId', '==', userId)
      .get();

    const batch = this.db.batch();

    snapshot.forEach((doc) => {
      // For each doc, add a delete operation to the batch
      batch.delete(doc.ref);
    });

    await batch.commit();
  }

  async comment(reviewId: string, comment: iComment): Promise<iComment> {
    const reviewCommentRef = this.db.collection(REVIEW_COLLECTION)
      .doc(reviewId)
      .collection(COMMENT_SUBCOLLECTION)
      .doc(comment.id);

    await reviewCommentRef.set(comment.toObject());

    return comment;
  }

  async getCommentsByReviewId(reviewId: string): Promise<iComment[]> {
    const snapshot = await this.db.collection(REVIEW_COLLECTION)
      .doc(reviewId)
      .collection(COMMENT_SUBCOLLECTION)
      .orderBy('createdAt', 'desc')
      .get();

    const comments: iComment[] = [];

    snapshot.forEach((commentObj) => {
      const commentData = commentObj.data();
      const comment = new Comment(
        commentData.id,
        commentData.userId,
        commentData.gamertag,
        commentData.text,
        commentData.createdAt,
      );
      comments.push(comment);
    });

    return comments;
  }

  async getAllReviews(lastReview: iReview | null, limit: number): Promise<iReview[]> {
    let snapshot;

    if (lastReview) {
      const last = await this.db.collection(REVIEW_COLLECTION)
        .doc(lastReview.id)
        .get();

      snapshot = await this.db.collection(REVIEW_COLLECTION)
        .orderBy('createdAt', 'desc')
        .startAfter(last)
        .limit(limit)
        .get();
    } else {
      snapshot = await this.db.collection(REVIEW_COLLECTION)
        .orderBy('createdAt', 'desc')
        .limit(limit)
        .get();
    }

    return this.buildReviewsFromSnapshot(snapshot);
  }

  async getAllReviewsByUserId(
    userId: string,
    lastReview: iReview | null,
    limit: number,
  ): Promise<iReview[]> {
    let snapshot;

    if (lastReview) {
      const last = await this.db.collection(REVIEW_COLLECTION)
        .doc(lastReview.id)
        .get();

      snapshot = await this.db.collection(REVIEW_COLLECTION)
        .where('userId', '==', userId)
        .orderBy('createdAt', 'desc')
        .startAfter(last)
        .limit(limit)
        .get();
    } else {
      snapshot = await this.db.collection(REVIEW_COLLECTION)
        .where('userId', '==', userId)
        .orderBy('createdAt', 'desc')
        .limit(limit)
        .get();
    }

    return this.buildReviewsFromSnapshot(snapshot);
  }

  private async buildReviewsFromSnapshot(snapshot: ReviewSnapshot): Promise<iReview[]> {
    if (snapshot.empty) {
      return [];
    }

    const reviews: iReview[] = [];
    const promises: Promise<void>[] = [];

    snapshot.forEach(async (game) => {
      const promise = async () => {
        const data = game.data();

        const likes = await this.getLikes(data.id);
        const share = data.share
          ? new Share(
            data.share.reviewId,
            data.share.reviewUserId,
            data.share.reviewGamertag,
          )
          : undefined;

        reviews.push(new Review(
          data.id,
          data.text,
          data.rating,
          data.gameId,
          data.gameName,
          data.userId,
          data.gamertag,
          data.createdAt,
          likes,
          undefined,
          share,
        ));
      };

      promises.push(promise());
    });

    // We must wait until all promises are resolved to proceed
    await Promise.all(promises);

    return reviews;
  }

  private async getLikes(docId: string) {
    const likesSub = await this.db.collection(REVIEW_COLLECTION)
      .doc(docId)
      .collection(LIKE_SUBCOLLECTION)
      .get();

    const likes: iLike[] = [];

    likesSub.forEach((likeObj) => {
      const likeData = likeObj.data();
      const like = new Like(likeData.id, likeData.userId, likeData.createdAt);
      likes.push(like);
    });

    return likes;
  }
}
